import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Edit,
  Save,
  X,
  Plus,
  GripVertical,
  Eye,
  EyeOff,
  RotateCcw,
  Image as ImageIcon,
  Play,
  MapPin,
  Users
} from 'lucide-react';
import { isVideoUrl } from '../../utils/imageUrl';
import { galleryService } from '@/services/galleryService';
import { Button } from '@/components/ui/Button/Button';
import { AssetSelector } from './AssetSelector';
import { AssetSequencer } from './AssetSequencer';
import { ImageCarousel } from './ImageCarousel';
import {
  GalleryDetails,
  GalleryType,
  GalleryStatus,
  UpdateGalleryRequest
} from '@/types/gallery';
import { useAuth } from '@/hooks/useAuth';
import { PROGRAM_OPTIONS, GALLERY_TYPE_OPTIONS } from '../../constants';

interface GalleryDetailProps {
  onBackClick: () => void;
}

export const GalleryDetail: React.FC<GalleryDetailProps> = ({ onBackClick }) => {
  const { galleryId } = useParams<{ galleryId: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editStatus, setEditStatus] = useState<GalleryStatus>('Active');
  const [editType, setEditType] = useState<GalleryType>('village_gallery');
  const [showAssetSelector, setShowAssetSelector] = useState(false);
  const [showAssetSequencer, setShowAssetSequencer] = useState(false);
  const [showCarousel, setShowCarousel] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);

  // Check user permissions
  const canModerate = user?.roles?.some(role => 
    ['GALLERY.MODERATOR', 'ADMIN'].includes(role)
  );

  // Fetch gallery details
  const {
    data: galleryResponse,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['gallery-details', galleryId],
    queryFn: () => galleryService.getGalleryDetails(Number(galleryId)),
    enabled: !!galleryId,
  });

  const gallery: GalleryDetails | undefined = galleryResponse?.data;

  // Update gallery mutation
  const updateGalleryMutation = useMutation({
    mutationFn: (data: UpdateGalleryRequest) => galleryService.updateGallery(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gallery-details', galleryId] });
      queryClient.invalidateQueries({ queryKey: ['galleries'] });
      setIsEditing(false);
    }
  });

  // Toggle gallery status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: (newStatus: 'Active' | 'Inactive') =>
      galleryService.updateGalleryStatus(Number(galleryId), newStatus),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gallery-details', galleryId] });
      queryClient.invalidateQueries({ queryKey: ['galleries'] });
    }
  });


  useEffect(() => {
    if (gallery) {
      setEditTitle(gallery.title);
      setEditDescription(gallery.description || '');
      setEditStatus(gallery.status);
      setEditType(gallery.type);
    }
  }, [gallery]);

  const handleSaveEdit = () => {
    if (!gallery) return;

    const updateData: UpdateGalleryRequest = {
      id: gallery.id,
      title: editTitle.trim(),
      description: editDescription.trim() || undefined,
      status: editStatus,
      type: editType,
    };

    updateGalleryMutation.mutate(updateData);
  };

  const handleCancelEdit = () => {
    if (gallery) {
      setEditTitle(gallery.title);
      setEditDescription(gallery.description || '');
      setEditStatus(gallery.status);
      setEditType(gallery.type);
    }
    setIsEditing(false);
  };


  const handleDeactivateAsset = (assetId: number) => {
    if (window.confirm('Are you sure you want to deactivate this asset?')) {
      const allAssets = gallery!.assets.map(asset => ({
        ...asset,
        status: asset.id === assetId ? 'Inactive' as const : asset.status
      }));

      // Re-sequence: active assets get continuous 1,2,3,...; inactive get sequence after active
      const active = allAssets.filter(a => a.status === 'Active').sort((a, b) => a.sequence_number - b.sequence_number);
      const inactive = allAssets.filter(a => a.status === 'Inactive');

      let seq = 1;
      const resequenced = [
        ...active.map(a => ({ id: a.id, sequence_number: seq++, asset_library_id: a.asset_library_id, status: a.status })),
        ...inactive.map(a => ({ id: a.id, sequence_number: seq++, asset_library_id: a.asset_library_id, status: a.status })),
      ];

      updateGalleryMutation.mutate({ id: gallery!.id, assets: resequenced });
    }
  };

  const handleReactivateAsset = (assetId: number) => {
    if (window.confirm('Are you sure you want to reactivate this asset?')) {
      const allAssets = gallery!.assets.map(asset => ({
        ...asset,
        status: asset.id === assetId ? 'Active' as const : asset.status
      }));

      // Re-sequence: active assets get continuous 1,2,3,...; inactive get sequence after active
      const active = allAssets.filter(a => a.status === 'Active').sort((a, b) => a.sequence_number - b.sequence_number);
      const inactive = allAssets.filter(a => a.status === 'Inactive');

      let seq = 1;
      const resequenced = [
        ...active.map(a => ({ id: a.id, sequence_number: seq++, asset_library_id: a.asset_library_id, status: a.status })),
        ...inactive.map(a => ({ id: a.id, sequence_number: seq++, asset_library_id: a.asset_library_id, status: a.status })),
      ];

      updateGalleryMutation.mutate({ id: gallery!.id, assets: resequenced });
    }
  };

  const handleToggleStatus = () => {
    if (!gallery) return;
    const newStatus = gallery.status === 'Active' ? 'Inactive' : 'Active';
    const action = newStatus === 'Inactive' ? 'deactivate' : 'activate';
    if (window.confirm(`Are you sure you want to ${action} this gallery?`)) {
      toggleStatusMutation.mutate(newStatus);
    }
  };

  const handleAssetsAdded = () => {
    setShowAssetSelector(false);
    refetch();
  };

  const handleSequenceUpdated = () => {
    setShowAssetSequencer(false);
    refetch();
  };

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-rgf-gray-200 rounded mb-4"></div>
          <div className="h-32 bg-rgf-gray-200 rounded mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-48 bg-rgf-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !gallery) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-800">
            Error loading gallery: {error?.message || 'Gallery not found'}
          </p>
          <Button variant="secondary" className="mt-4" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const activeAssets = gallery.assets.filter(asset => asset.status === 'Active');
  const inactiveAssets = gallery.assets.filter(asset => asset.status === 'Inactive');

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white shadow-sm border border-rgf-gray-200 rounded-lg p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center mb-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={onBackClick}
                icon={<ArrowLeft className="h-4 w-4" />}
              >
                Back to Galleries
              </Button>
            </div>

            {isEditing ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-1">Title *</label>
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="text-lg font-semibold text-rgf-gray-900 bg-white border border-rgf-gray-300 rounded-lg px-3 py-2 w-full focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-1">Description</label>
                  <textarea
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="Enter gallery description (optional)"
                    rows={3}
                    className="block w-full border border-rgf-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-rgf-gray-700 mb-1">Gallery Type</label>
                    <select
                      value={editType}
                      onChange={(e) => setEditType(e.target.value as GalleryType)}
                      className="block w-full border border-rgf-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                    >
                      {GALLERY_TYPE_OPTIONS.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-rgf-gray-700 mb-1">Status</label>
                    <select
                      value={editStatus}
                      onChange={(e) => setEditStatus(e.target.value as GalleryStatus)}
                      className="block w-full border border-rgf-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Button
                    size="sm"
                    onClick={handleSaveEdit}
                    loading={updateGalleryMutation.isPending}
                    icon={<Save className="h-4 w-4" />}
                  >
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={handleCancelEdit}
                    icon={<X className="h-4 w-4" />}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-rgf-gray-900">
                    {gallery.title}
                  </h1>
                  {gallery.description && (
                    <p className="mt-2 text-rgf-gray-600">{gallery.description}</p>
                  )}
                </div>
                {canModerate && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                    icon={<Edit className="h-4 w-4" />}
                  >
                    Edit
                  </Button>
                )}
              </div>
            )}

            {/* Gallery Info */}
            <div className="mt-4 flex flex-wrap items-center gap-6 text-sm text-rgf-gray-500">
              <div className="flex items-center">
                <MapPin className="h-4 w-4 mr-1" />
                <span>{gallery.village_name}, {gallery.subdistrict_name}, {gallery.district_name}</span>
              </div>
              
              <div className="flex items-center">
                <Users className="h-4 w-4 mr-1" />
                <span>Program: {PROGRAM_OPTIONS.find(p => p.value === gallery.program_id)?.label || gallery.program_id}</span>
              </div>

              <div className="flex items-center">
                <span>{GALLERY_TYPE_OPTIONS.find(t => t.value === gallery.type)?.label || gallery.type}</span>
              </div>

              <div className="flex items-center">
                <ImageIcon className="h-4 w-4 mr-1" />
                <span>{activeAssets.length} assets</span>
              </div>

              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                gallery.status === 'Active' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {gallery.status}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Gallery Actions */}
      {canModerate && (
        <div className="bg-white shadow-sm border border-rgf-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-rgf-gray-900">Gallery Management</h3>
            <div className="flex items-center space-x-3">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowAssetSelector(true)}
                icon={<Plus className="h-4 w-4" />}
              >
                Add Assets
              </Button>

              {activeAssets.length > 1 && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowAssetSequencer(true)}
                  icon={<GripVertical className="h-4 w-4" />}
                >
                  Reorder Assets
                </Button>
              )}

              {gallery.status === 'Active' ? (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleToggleStatus}
                  loading={toggleStatusMutation.isPending}
                  icon={<EyeOff className="h-4 w-4" />}
                  className="text-orange-600 hover:bg-orange-50"
                >
                  Deactivate Gallery
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleToggleStatus}
                  loading={toggleStatusMutation.isPending}
                  icon={<Eye className="h-4 w-4" />}
                  className="text-green-600 hover:bg-green-50"
                >
                  Activate Gallery
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Gallery Assets */}
      <div className="bg-white shadow-sm border border-rgf-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-rgf-gray-900">
            Gallery Assets
          </h3>
          <div className="flex items-center space-x-4 text-sm text-rgf-gray-500">
            <span>{activeAssets.length} Active</span>
            {inactiveAssets.length > 0 && (
              <span>{inactiveAssets.length} Inactive</span>
            )}
            <span>{gallery.assets.length} Total</span>
          </div>
        </div>

        {activeAssets.length === 0 ? (
          <div className="text-center py-12">
            <ImageIcon className="mx-auto h-12 w-12 text-rgf-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-rgf-gray-900">No assets</h3>
            <p className="mt-1 text-sm text-rgf-gray-500">
              Add photos and videos to this gallery to showcase program impact.
            </p>
            {canModerate && (
              <div className="mt-6">
                <Button 
                  onClick={() => setShowAssetSelector(true)}
                  icon={<Plus className="h-4 w-4" />}
                >
                  Add Assets
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {/* Active Assets */}
            <div>
              <h4 className="text-md font-medium text-rgf-gray-900 mb-3">
                Active Assets ({activeAssets.length})
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {activeAssets
                  .sort((a, b) => a.sequence_number - b.sequence_number)
                  .map((asset) => (
                <div
                  key={asset.id}
                  className="relative group bg-rgf-gray-50 rounded-lg overflow-hidden cursor-pointer"
                  onClick={() => {
                    const assetIndex = activeAssets
                      .sort((a, b) => a.sequence_number - b.sequence_number)
                      .findIndex(a => a.id === asset.id);
                    setCarouselIndex(assetIndex);
                    setShowCarousel(true);
                  }}
                >
                  <div className="aspect-square">
                    {isVideoUrl(asset.cdn_url) ? (
                      <video
                        src={asset.cdn_url}
                        muted
                        playsInline
                        preload="metadata"
                        className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                        onError={(e) => {
                          (e.target as HTMLVideoElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <img
                        src={asset.cdn_url}
                        alt={asset.asset_id}
                        className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                        loading="lazy"
                        onError={(e) => {
                          const img = e.target as HTMLImageElement;
                          if (!img.dataset.fallback) {
                            img.dataset.fallback = '1';
                            img.src = '/placeholder-image.svg';
                          }
                        }}
                      />
                    )}
                  </div>

                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200">
                    <div className="absolute top-2 left-2 flex items-center space-x-1">
                      <span className="inline-flex items-center justify-center w-6 h-6 bg-white rounded-full text-xs font-medium text-rgf-gray-900">
                        {asset.sequence_number}
                      </span>
                      {isVideoUrl(asset.cdn_url) && (
                        <span className="inline-flex items-center justify-center bg-black bg-opacity-70 text-white px-1.5 py-0.5 rounded text-xs">
                          <Play className="h-3 w-3 mr-0.5" />Video
                        </span>
                      )}
                    </div>

                    {/* Click to view overlay */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <div className="bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-sm font-medium">
                        {isVideoUrl(asset.cdn_url) ? 'Click to play' : 'Click to view'}
                      </div>
                    </div>

                    {canModerate && (
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        <div className="flex space-x-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="!p-1 bg-white text-rgf-gray-700 hover:bg-rgf-gray-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(asset.cdn_url, '_blank');
                            }}
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                          {asset.status === 'Active' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="!p-1 bg-white text-orange-600 hover:bg-orange-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeactivateAsset(asset.id);
                              }}
                              loading={updateGalleryMutation.isPending}
                              title="Deactivate asset"
                            >
                              <EyeOff className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Inactive Assets */}
            {inactiveAssets.length > 0 && (
              <div>
                <h4 className="text-md font-medium text-rgf-gray-500 mb-3">
                  Inactive Assets ({inactiveAssets.length})
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {inactiveAssets
                    .sort((a, b) => a.sequence_number - b.sequence_number)
                    .map((asset) => (
                      <div
                        key={asset.id}
                        className="relative group bg-rgf-gray-50 rounded-lg overflow-hidden opacity-60"
                      >
                        <div className="aspect-square">
                          {isVideoUrl(asset.cdn_url) ? (
                            <video
                              src={asset.cdn_url}
                              muted
                              playsInline
                              preload="metadata"
                              className="w-full h-full object-cover grayscale"
                              onError={(e) => {
                                (e.target as HTMLVideoElement).style.display = 'none';
                              }}
                            />
                          ) : (
                            <img
                              src={asset.cdn_url}
                              alt={asset.asset_id}
                              className="w-full h-full object-cover grayscale"
                              loading="lazy"
                              onError={(e) => {
                                const img = e.target as HTMLImageElement;
                                if (!img.dataset.fallback) {
                                  img.dataset.fallback = '1';
                                  img.src = '/placeholder-image.svg';
                                }
                              }}
                            />
                          )}
                        </div>
                        
                        <div className="absolute inset-0 bg-black bg-opacity-20">
                          <div className="absolute top-2 left-2">
                            <span className="inline-flex items-center justify-center w-6 h-6 bg-gray-500 rounded-full text-xs font-medium text-white">
                              {asset.sequence_number}
                            </span>
                          </div>
                          
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-sm font-medium">
                              Inactive
                            </div>
                          </div>
                          
                          {canModerate && (
                            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                              <div className="flex space-x-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="!p-1 bg-white text-rgf-gray-700 hover:bg-rgf-gray-100"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(asset.cdn_url, '_blank');
                                  }}
                                >
                                  <Eye className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="!p-1 bg-white text-green-600 hover:bg-green-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleReactivateAsset(asset.id);
                                  }}
                                  loading={updateGalleryMutation.isPending}
                                  title="Reactivate asset"
                                >
                                  <RotateCcw className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Asset Selector Modal */}
      {showAssetSelector && (
        <AssetSelector
          galleryId={gallery.id}
          onClose={() => setShowAssetSelector(false)}
          onAssetsAdded={handleAssetsAdded}
        />
      )}

      {/* Asset Sequencer Modal */}
      {showAssetSequencer && (
        <AssetSequencer
          galleryId={gallery.id}
          assets={activeAssets}
          onClose={() => setShowAssetSequencer(false)}
          onSequenceUpdated={handleSequenceUpdated}
        />
      )}

      {/* Image Carousel */}
      {showCarousel && activeAssets.length > 0 && (
        <ImageCarousel
          assets={activeAssets.sort((a, b) => a.sequence_number - b.sequence_number)}
          initialIndex={carouselIndex}
          onClose={() => setShowCarousel(false)}
          galleryTitle={gallery.title}
          galleryLocation={{
            village: gallery.village_name,
            subdistrict: gallery.subdistrict_name,
            district: gallery.district_name
          }}
        />
      )}
    </div>
  );
};